import { useState, useEffect, useCallback, useRef } from 'react';
import {
  apiClient,
  SoakTestConfig,
  SoakTestStatus,
  SoakLog,
  SoakLogSeverity,
  SoakLogSource,
} from '../api/client';
import { MetricCardSkeleton, CardSkeleton } from '../components/Skeleton';
import { toast } from '../components/Toast';
import { CrashModal } from '../components/CrashModal';

// Test history run interface
interface TestRun {
  id: string;
  started_at: number;
  ended_at: number | null;
  duration_hours: number;
  status: string;
  environment: string;
  config: SoakTestConfig;
  metrics: Record<string, unknown> | null;
}

const DURATION_PRESETS = [
  { label: '1 hour', value: 1 },
  { label: '4 hours', value: 4 },
  { label: '8 hours', value: 8 },
  { label: '24 hours', value: 24 },
  { label: 'Custom', value: -1 },
];

const SEVERITY_OPTIONS: SoakLogSeverity[] = ['ALL', 'INFO', 'WARN', 'ERROR', 'CRITICAL'];
const SOURCE_OPTIONS: SoakLogSource[] = ['all', 'orchestrator', 'android', 'cloudflare'];

const SEVERITY_COLORS: Record<string, string> = {
  INFO: '#757575',
  WARN: '#ff9800',
  ERROR: '#f44336',
  CRITICAL: '#d32f2f',
};

export function SoakTestPage() {
  // Status state
  const [status, setStatus] = useState<SoakTestStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Configuration state
  const [config, setConfig] = useState<SoakTestConfig>({
    duration_hours: 1,
    audio_interval_minutes: 10,
    channels: ['general'],
    environment: 'development',
  });
  const [durationPreset, setDurationPreset] = useState<number>(1);
  const [customDuration, setCustomDuration] = useState<number>(1);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // Logs state
  const [logs, setLogs] = useState<SoakLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logSeverity, setLogSeverity] = useState<SoakLogSeverity>('ALL');
  const [logSource, setLogSource] = useState<SoakLogSource>('all');
  const [logSearch, setLogSearch] = useState('');
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Expanded device row
  const [expandedDevice, setExpandedDevice] = useState<string | null>(null);

  // Crash modal
  const [selectedCrashId, setSelectedCrashId] = useState<string | null>(null);

  // Webhook configuration
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>(['crash', 'device_offline']);

  // Test history state
  const [testHistory, setTestHistory] = useState<TestRun[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [selectedTestLogs, setSelectedTestLogs] = useState<SoakLog[]>([]);
  const [selectedTestLogsTotal, setSelectedTestLogsTotal] = useState(0);
  const [isLoadingSelectedLogs, setIsLoadingSelectedLogs] = useState(false);
  const [historyLogSeverity, setHistoryLogSeverity] = useState<SoakLogSeverity>('ALL');
  const [historyLogSource, setHistoryLogSource] = useState<SoakLogSource>('all');

  // Refs
  const logContainerRef = useRef<HTMLDivElement>(null);
  const statusIntervalRef = useRef<number | null>(null);
  const logsIntervalRef = useRef<number | null>(null);

  // Load status
  const loadStatus = useCallback(async () => {
    try {
      const response = await apiClient.getSoakTestStatus();
      if (response.success && response.data) {
        // Normalize API response to match expected SoakTestStatus interface
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rawData = response.data as any;
        const normalizedStatus: SoakTestStatus = {
          test_id: rawData.test_id || null,
          status: rawData.status || 'idle',
          started_at: rawData.started_at || null,
          duration_hours: rawData.duration_hours || 0,
          elapsed_ms: rawData.elapsed_ms || 0,
          metrics: {
            devices_active: rawData.metrics?.devices_active || 0,
            transcripts_sent: rawData.metrics?.audio_chunks_sent || rawData.metrics?.transcripts_sent || 0,
            transcripts_received: rawData.metrics?.transcripts_received || 0,
            avg_latency_ms: rawData.metrics?.avg_latency_ms || 0,
            errors: rawData.metrics?.error_count || rawData.metrics?.errors || 0,
            crashes: rawData.metrics?.crashes || 0,
            missing_transcripts: rawData.metrics?.transcripts_missing || rawData.metrics?.missing_transcripts || 0,
            latency_breakdown: rawData.metrics?.latency_breakdown,
          },
          devices: rawData.devices || [],
        };
        setStatus(normalizedStatus);
      }
    } catch {
      // Silent fail for polling
    }
  }, []);

  // Load logs
  const loadLogs = useCallback(async () => {
    if (!status?.test_id) return;

    setIsLoadingLogs(true);
    try {
      const response = await apiClient.getSoakLogs({
        test_id: status.test_id,
        limit: 200,
        severity: logSeverity,
        source: logSource,
        search: logSearch || undefined,
      });
      if (response.success && response.data) {
        setLogs(response.data.logs);
        setLogsTotal(response.data.total);

        // Auto-scroll to bottom
        if (isAutoScroll && logContainerRef.current) {
          setTimeout(() => {
            if (logContainerRef.current) {
              logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
            }
          }, 0);
        }
      }
    } catch {
      // Silent fail for polling
    }
    setIsLoadingLogs(false);
  }, [status?.test_id, logSeverity, logSource, logSearch, isAutoScroll]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setError(null);
      try {
        await loadStatus();
      } catch {
        setError('Failed to load soak test status');
        toast.error('Failed to load soak test status');
      }
      setIsLoading(false);
    };
    init();
  }, [loadStatus]);

  // Auto-refresh status every 10 seconds
  useEffect(() => {
    statusIntervalRef.current = window.setInterval(loadStatus, 10000);
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, [loadStatus]);

  // Auto-refresh logs every 5 seconds when running
  useEffect(() => {
    if (status?.status === 'running' && status.test_id) {
      loadLogs();
      logsIntervalRef.current = window.setInterval(loadLogs, 5000);
    }
    return () => {
      if (logsIntervalRef.current) {
        clearInterval(logsIntervalRef.current);
      }
    };
  }, [status?.status, status?.test_id, loadLogs]);

  // Reload logs when filters change
  useEffect(() => {
    if (status?.test_id) {
      loadLogs();
    }
  }, [logSeverity, logSource, logSearch, status?.test_id, loadLogs]);

  // Load test history
  const loadTestHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const response = await apiClient.getSoakTestHistory(50);
      if (response.success && response.data) {
        setTestHistory(response.data.runs);
      }
    } catch {
      toast.error('Failed to load test history');
    }
    setIsLoadingHistory(false);
  }, []);

  // Load selected test logs
  const loadSelectedTestLogs = useCallback(async () => {
    if (!selectedTestId) return;
    setIsLoadingSelectedLogs(true);
    try {
      const response = await apiClient.getSoakTestLogs(selectedTestId, {
        limit: 500,
        severity: historyLogSeverity,
        source: historyLogSource,
      });
      if (response.success && response.data) {
        setSelectedTestLogs(response.data.logs);
        setSelectedTestLogsTotal(response.data.total);
      }
    } catch {
      toast.error('Failed to load test logs');
    }
    setIsLoadingSelectedLogs(false);
  }, [selectedTestId, historyLogSeverity, historyLogSource]);

  // Load history on mount
  useEffect(() => {
    loadTestHistory();
  }, [loadTestHistory]);

  // Reload selected test logs when filters change
  useEffect(() => {
    if (selectedTestId) {
      loadSelectedTestLogs();
    }
  }, [selectedTestId, historyLogSeverity, historyLogSource, loadSelectedTestLogs]);

  // Handle start test
  const handleStartTest = async () => {
    setIsStarting(true);
    setError(null);

    const duration = durationPreset === -1 ? customDuration : durationPreset;
    const testConfig: SoakTestConfig = {
      ...config,
      duration_hours: duration,
      webhook_url: webhookUrl || undefined,
      webhook_events: webhookEvents.length > 0 ? webhookEvents as SoakTestConfig['webhook_events'] : undefined,
    };

    try {
      const response = await apiClient.startSoakTest(testConfig);
      if (response.success && response.data) {
        toast.success(`Soak test started: ${response.data.test_id}`);
        await loadStatus();
      } else {
        setError(response.error || 'Failed to start soak test');
        toast.error(response.error || 'Failed to start soak test');
      }
    } catch {
      setError('Failed to start soak test');
      toast.error('Failed to start soak test');
    }
    setIsStarting(false);
  };

  // Handle stop test
  const handleStopTest = async () => {
    setIsStopping(true);
    try {
      const response = await apiClient.stopSoakTest();
      if (response.success) {
        toast.success('Soak test stopped');
        await loadStatus();
      } else {
        toast.error(response.error || 'Failed to stop soak test');
      }
    } catch {
      toast.error('Failed to stop soak test');
    }
    setIsStopping(false);
  };

  // Handle download logs
  const handleDownloadLogs = async () => {
    if (!status?.test_id) return;

    try {
      const blob = await apiClient.downloadSoakLogs(status.test_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soak-test-${status.test_id}-logs.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Logs downloaded');
    } catch {
      toast.error('Failed to download logs');
    }
  };

  // Handle download history test logs
  const handleDownloadHistoryLogs = async (testId: string, format: 'json' | 'csv') => {
    try {
      const blob = await apiClient.downloadSoakTestLogs(testId, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soak-logs-${testId}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Logs downloaded as ${format.toUpperCase()}`);
    } catch {
      toast.error('Failed to download logs');
    }
  };

  // Format date for history
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle delete test
  const handleDeleteTest = async (testId: string) => {
    if (!confirm(`Delete test ${testId}? This will remove all logs and data for this test.`)) {
      return;
    }
    try {
      const response = await apiClient.deleteSoakTest(testId);
      if (response.success) {
        toast.success('Test deleted');
        if (selectedTestId === testId) {
          setSelectedTestId(null);
          setSelectedTestLogs([]);
        }
        await loadTestHistory();
      } else {
        toast.error(response.error || 'Failed to delete test');
      }
    } catch {
      toast.error('Failed to delete test');
    }
  };

  // Format duration
  const formatDuration = (startMs: number, endMs: number | null): string => {
    const end = endMs || Date.now();
    const durationMs = end - startMs;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Format elapsed time
  const formatElapsedTime = (ms: number): string => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format timestamp for logs
  const formatLogTimestamp = (ts: number): string => {
    return new Date(ts).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Format last seen
  const formatLastSeen = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  // Get status badge color
  const getStatusColor = (testStatus: string): string => {
    switch (testStatus) {
      case 'running':
        return 'var(--primary)';
      case 'completed':
        return 'var(--success)';
      case 'failed':
        return 'var(--error)';
      case 'stopped':
        return 'var(--warning)';
      case 'idle':
      default:
        return 'var(--text-secondary)';
    }
  };

  // Get device status color
  const getDeviceStatusColor = (deviceStatus: string): string => {
    switch (deviceStatus) {
      case 'healthy':
      case 'online':
        return 'var(--success)';
      case 'warning':
        return 'var(--warning)';
      case 'error':
      case 'offline':
      case 'crashed':
        return 'var(--error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  // Handle webhook event toggle
  const toggleWebhookEvent = (event: string) => {
    setWebhookEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  if (isLoading) {
    return (
      <div className="soak-test-page">
        <h1 className="page-title">Soak Test Control</h1>
        <div className="metrics-grid">
          {[1, 2, 3, 4].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <CardSkeleton lines={4} />
        <CardSkeleton lines={6} />
      </div>
    );
  }

  const isRunning = status?.status === 'running';
  const showConfig = !isRunning; // Show config for idle, stopped, completed, failed, or null status

  return (
    <div className="soak-test-page">
      {/* Header */}
      <div className="page-header soak-header">
        <h1 className="page-title">Soak Test Control</h1>
        <div className="soak-header-actions">
          <div
            className="soak-status-badge"
            style={{ backgroundColor: getStatusColor(status?.status || 'idle') }}
          >
            {status?.status?.toUpperCase() || 'NOT RUNNING'}
          </div>
          {isRunning && (
            <div className="soak-elapsed">
              <span className="soak-elapsed-label">Elapsed:</span>
              <span className="soak-elapsed-time">{formatElapsedTime(status?.elapsed_ms || 0)}</span>
            </div>
          )}
        </div>
      </div>

      {error && <div className="card error-card">{error}</div>}

      {/* Configuration Panel */}
      {showConfig && (
        <div className="card">
          <h2 className="section-title">Test Configuration</h2>

          <div className="soak-config-grid">
            {/* Duration */}
            <div className="form-group">
              <label>Duration</label>
              <div className="duration-selector">
                {DURATION_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    className={`duration-btn ${durationPreset === preset.value ? 'active' : ''}`}
                    onClick={() => setDurationPreset(preset.value)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              {durationPreset === -1 && (
                <div className="custom-duration">
                  <input
                    type="number"
                    className="input"
                    value={customDuration}
                    onChange={(e) => setCustomDuration(parseInt(e.target.value, 10) || 1)}
                    min={1}
                    max={168}
                  />
                  <span>hours</span>
                </div>
              )}
            </div>

            {/* Audio Interval */}
            <div className="form-group">
              <label>Audio Interval</label>
              <p className="form-hint">How often to send test audio</p>
              <div className="interval-input">
                <input
                  type="number"
                  className="input"
                  value={config.audio_interval_minutes}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      audio_interval_minutes: parseInt(e.target.value, 10) || 10,
                    }))
                  }
                  min={1}
                  max={60}
                />
                <span>minutes</span>
              </div>
            </div>

            {/* Environment */}
            <div className="form-group">
              <label>Environment</label>
              <div className="environment-selector">
                <button className="env-btn active">Development</button>
                <button className="env-btn" disabled title="Coming soon">
                  Staging
                </button>
                <button className="env-btn" disabled title="Coming soon">
                  Production
                </button>
              </div>
            </div>

            {/* Webhook Configuration */}
            <div className="form-group form-group-full">
              <label>Webhook Notifications (Optional)</label>
              <p className="form-hint">Get notified of critical events during the test</p>
              <div className="webhook-config">
                <input
                  type="url"
                  className="input webhook-url-input"
                  placeholder="https://your-webhook-endpoint.com/soak-events"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
                <div className="webhook-events">
                  {['crash', 'device_offline', 'test_failure', 'test_complete'].map((event) => (
                    <label key={event} className="webhook-event-checkbox">
                      <input
                        type="checkbox"
                        checked={webhookEvents.includes(event)}
                        onChange={() => toggleWebhookEvent(event)}
                      />
                      <span>{event.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="soak-start-section">
            <button className="button soak-start-btn" onClick={handleStartTest} disabled={isStarting}>
              {isStarting ? 'Starting...' : 'Start Soak Test'}
            </button>
          </div>
        </div>
      )}

      {/* Live Metrics Dashboard */}
      {isRunning && status && (
        <div className="card">
          <div className="section-header">
            <h2 className="section-title">Live Metrics</h2>
            <button className="button button-secondary soak-stop-btn" onClick={handleStopTest} disabled={isStopping}>
              {isStopping ? 'Stopping...' : 'Stop Test'}
            </button>
          </div>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-value">{status.metrics.devices_active}</span>
              <span className="metric-label">Active Devices</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{status.metrics.transcripts_sent}</span>
              <span className="metric-label">Transcripts Sent</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{status.metrics.transcripts_received}</span>
              <span className="metric-label">Transcripts Received</span>
            </div>
            <div className="metric-card">
              <span className={`metric-value ${status.metrics.errors > 0 ? 'metric-warning' : ''}`}>
                {status.metrics.errors}
              </span>
              <span className="metric-label">Errors</span>
            </div>
            <div className="metric-card">
              <span className={`metric-value ${status.metrics.crashes > 0 ? 'metric-warning' : ''}`}>
                {status.metrics.crashes}
              </span>
              <span className="metric-label">Crashes</span>
            </div>
            <div className="metric-card">
              <span
                className={`metric-value ${status.metrics.missing_transcripts > 0 ? 'metric-warning' : ''}`}
              >
                {status.metrics.missing_transcripts}
              </span>
              <span className="metric-label">Missing</span>
            </div>
          </div>

          {/* Latency Breakdown */}
          <div className="soak-latency-box">
            <h3 className="soak-latency-title">Latency</h3>
            <div className="soak-latency-grid">
              <div className="soak-latency-item">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.tts_ms ?? 0}ms
                </span>
                <span className="soak-latency-label">TTS</span>
              </div>
              <div className="soak-latency-item">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.processing_ms ?? 0}ms
                </span>
                <span className="soak-latency-label">Processing</span>
              </div>
              <div className="soak-latency-item">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.broadcast_ms ?? 0}ms
                </span>
                <span className="soak-latency-label">Broadcast</span>
              </div>
              <div className="soak-latency-item">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.whisper_ms ?? 0}ms
                </span>
                <span className="soak-latency-label">Whisper</span>
              </div>
              <div className="soak-latency-item soak-latency-total">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.total_ms ?? status.metrics.avg_latency_ms.toFixed(0)}ms
                </span>
                <span className="soak-latency-label">Total</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Status Table */}
      {isRunning && status && status.devices.length > 0 && (
        <div className="card">
          <h2 className="section-title">Device Status</h2>
          <div className="soak-device-list">
            <div className="soak-device-header">
              <span>Device ID</span>
              <span>Status</span>
              <span>Battery</span>
              <span>Screen</span>
              <span>Last Seen</span>
              <span>Transcripts</span>
            </div>
            {status.devices.map((device) => (
              <div key={device.device_id}>
                <div
                  className={`soak-device-row ${expandedDevice === device.device_id ? 'expanded' : ''}`}
                  onClick={() =>
                    setExpandedDevice(expandedDevice === device.device_id ? null : device.device_id)
                  }
                >
                  <span className="device-id">{device.device_id.slice(0, 8)}...</span>
                  <span className="device-status" style={{ color: getDeviceStatusColor(device.status) }}>
                    {device.status}
                  </span>
                  <span className={`battery-level ${device.battery_level < 20 ? 'low' : ''}`}>
                    {device.battery_level}%
                    {device.is_plugged_in && ' ⚡'}
                  </span>
                  <span>{device.is_screen_on ? 'On' : 'Off'}</span>
                  <span>{formatLastSeen(device.last_seen)}</span>
                  <span>{device.transcripts_received}</span>
                </div>
                {expandedDevice === device.device_id && (
                  <div className="soak-device-details">
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Full Device ID</span>
                        <span className="detail-value detail-mono">{device.device_id}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Charging</span>
                        <span className="detail-value">{device.is_plugged_in ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Battery Level</span>
                        <span className="detail-value">{device.battery_level}%</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Screen State</span>
                        <span className="detail-value">{device.is_screen_on ? 'On' : 'Off'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Log Viewer */}
      {(isRunning || status?.status === 'completed' || status?.status === 'failed') && status?.test_id && (
        <div className="card soak-logs-card">
          <div className="section-header">
            <h2 className="section-title">Logs ({logsTotal})</h2>
            <div className="log-actions">
              <button
                className={`button button-small ${isAutoScroll ? 'active' : 'button-secondary'}`}
                onClick={() => setIsAutoScroll(!isAutoScroll)}
              >
                {isAutoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
              </button>
              <button className="button button-small button-secondary" onClick={handleDownloadLogs}>
                Download Logs
              </button>
            </div>
          </div>

          {/* Log Filters */}
          <div className="log-filters">
            <div className="log-filter-group">
              <label>Severity:</label>
              <select
                className="input log-filter-select"
                value={logSeverity}
                onChange={(e) => setLogSeverity(e.target.value as SoakLogSeverity)}
              >
                {SEVERITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="log-filter-group">
              <label>Source:</label>
              <select
                className="input log-filter-select"
                value={logSource}
                onChange={(e) => setLogSource(e.target.value as SoakLogSource)}
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="log-filter-group log-search-group">
              <label>Search:</label>
              <input
                type="text"
                className="input"
                placeholder="Search logs..."
                value={logSearch}
                onChange={(e) => setLogSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Log Viewer */}
          <div className="log-viewer" ref={logContainerRef}>
            {isLoadingLogs && logs.length === 0 ? (
              <div className="log-loading">Loading logs...</div>
            ) : logs.length === 0 ? (
              <div className="log-empty">No logs found</div>
            ) : (
              logs.map((log) => (
                <div key={log.id} className="log-entry" style={{ borderLeftColor: SEVERITY_COLORS[log.severity] }}>
                  <span className="log-timestamp">[{formatLogTimestamp(log.timestamp)}]</span>
                  <span className="log-severity" style={{ color: SEVERITY_COLORS[log.severity] }}>
                    [{log.severity}]
                  </span>
                  <span className="log-source">[{log.source}]</span>
                  <span className="log-message">{log.message}</span>
                  {log.device_id && <span className="log-device">({log.device_id.slice(0, 8)})</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Results Summary (for completed/failed tests) */}
      {(status?.status === 'completed' || status?.status === 'failed') && status?.metrics && (
        <div className="card">
          <h2 className="section-title">Test Results</h2>
          <div className={`soak-result-banner ${status.status}`}>
            {status.status === 'completed' ? 'Test Completed Successfully' : 'Test Failed'}
          </div>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-value">{formatElapsedTime(status.elapsed_ms)}</span>
              <span className="metric-label">Total Duration</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{status.metrics.devices_active}</span>
              <span className="metric-label">Devices Tested</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">
                {status.metrics.transcripts_sent > 0
                  ? ((status.metrics.transcripts_received / status.metrics.transcripts_sent) * 100).toFixed(1)
                  : 0}
                %
              </span>
              <span className="metric-label">Delivery Rate</span>
            </div>
          </div>

          {/* Latency Breakdown */}
          <div className="soak-latency-box">
            <h3 className="soak-latency-title">Latency</h3>
            <div className="soak-latency-grid">
              <div className="soak-latency-item">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.tts_ms ?? 0}ms
                </span>
                <span className="soak-latency-label">TTS</span>
              </div>
              <div className="soak-latency-item">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.processing_ms ?? 0}ms
                </span>
                <span className="soak-latency-label">Processing</span>
              </div>
              <div className="soak-latency-item">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.broadcast_ms ?? 0}ms
                </span>
                <span className="soak-latency-label">Broadcast</span>
              </div>
              <div className="soak-latency-item">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.whisper_ms ?? 0}ms
                </span>
                <span className="soak-latency-label">Whisper</span>
              </div>
              <div className="soak-latency-item soak-latency-total">
                <span className="soak-latency-value">
                  {status.metrics.latency_breakdown?.total_ms ?? status.metrics.avg_latency_ms.toFixed(0)}ms
                </span>
                <span className="soak-latency-label">Total</span>
              </div>
            </div>
          </div>
          <div className="soak-new-test">
            <button className="button" onClick={() => loadStatus()}>
              Start New Test
            </button>
          </div>
        </div>
      )}

      {/* Test History */}
      <div className="card">
        <div className="section-header">
          <h2 className="section-title">Test History</h2>
          <button className="button button-secondary" onClick={loadTestHistory} disabled={isLoadingHistory}>
            {isLoadingHistory ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {testHistory.length === 0 ? (
          <div className="soak-history-empty">No previous tests found</div>
        ) : (
          <div className="soak-history-list">
            <div className="soak-history-header">
              <span>Test ID</span>
              <span>Started</span>
              <span>Duration</span>
              <span>Status</span>
              <span>Environment</span>
              <span>Actions</span>
            </div>
            {testHistory.map((test) => (
              <div
                key={test.id}
                className={`soak-history-row ${selectedTestId === test.id ? 'selected' : ''} ${test.status === 'running' ? 'running' : ''}`}
              >
                <span className="soak-history-id" title={test.id}>
                  {test.id.slice(5, 20)}...
                  {test.status === 'running' && <span className="running-indicator" />}
                </span>
                <span>{formatDate(test.started_at)}</span>
                <span>{formatDuration(test.started_at, test.ended_at)}</span>
                <span className={`soak-history-status ${test.status}`}>{test.status}</span>
                <span>{test.environment}</span>
                <span className="soak-history-actions">
                  <button
                    className="button button-small"
                    onClick={() => setSelectedTestId(selectedTestId === test.id ? null : test.id)}
                  >
                    {selectedTestId === test.id ? 'Hide' : 'View'}
                  </button>
                  <button
                    className="button button-small button-secondary"
                    onClick={() => handleDownloadHistoryLogs(test.id, 'json')}
                  >
                    JSON
                  </button>
                  <button
                    className="button button-small button-secondary"
                    onClick={() => handleDownloadHistoryLogs(test.id, 'csv')}
                  >
                    CSV
                  </button>
                  <button
                    className="button button-small button-danger"
                    onClick={() => handleDeleteTest(test.id)}
                    disabled={test.status === 'running'}
                    title={test.status === 'running' ? 'Stop test before deleting' : 'Delete test'}
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Selected Test Log Viewer */}
        {selectedTestId && (
          <div className="soak-history-logs">
            <div className="section-header">
              <h3 className="section-title">Logs for {selectedTestId.slice(5, 25)}...</h3>
              <span className="log-count">{selectedTestLogsTotal} logs</span>
            </div>
            <div className="log-filters">
              <select
                value={historyLogSeverity}
                onChange={(e) => setHistoryLogSeverity(e.target.value as SoakLogSeverity)}
              >
                {SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <select
                value={historyLogSource}
                onChange={(e) => setHistoryLogSource(e.target.value as SoakLogSource)}
              >
                {SOURCE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="log-viewer history-log-viewer">
              {isLoadingSelectedLogs ? (
                <div className="log-loading">Loading logs...</div>
              ) : selectedTestLogs.length === 0 ? (
                <div className="log-empty">No logs found</div>
              ) : (
                selectedTestLogs.map((log) => (
                  <div key={log.id} className="log-entry" style={{ borderLeftColor: SEVERITY_COLORS[log.severity] }}>
                    <span className="log-timestamp">[{formatLogTimestamp(log.timestamp)}]</span>
                    <span className="log-severity" style={{ color: SEVERITY_COLORS[log.severity] }}>
                      [{log.severity}]
                    </span>
                    <span className="log-source">[{log.source}]</span>
                    <span className="log-message">{log.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Crash Modal */}
      {selectedCrashId && (
        <CrashModal crashId={selectedCrashId} onClose={() => setSelectedCrashId(null)} />
      )}
    </div>
  );
}
